import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

const CarouselStyle = styled(Box)(
  ({ theme }) => `
  width: 100%;
  position: relative;
  touch-action: pan-y pinch-zoom; /* Allow vertical scroll, prevent horizontal swipe default */
  user-select: none; /* Prevent text selection during swipe */
  -webkit-user-select: none;

  .carousel-wrapper {
    position: relative;
    margin: 48px 0px;
    max-width: 1200px;
    margin: 48px auto;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 380px;
  }

  .carousel-track {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    gap: 24px;
    overflow: hidden; /* Prevent items from overflowing outside */
  }

  /* Arrows positioned at wrapper level - centered vertically */
  .arrow-left,
  .arrow-right {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;

    /* Override arrow button color to primaryV2 */
    .arrow-button {
      color: ${theme.palette.primaryV2.main};
      min-width: 60px;
      min-height: 60px;
    }
  }

  .arrow-left {
    left: -18px;
  }

  .arrow-right {
    right: -18px;
  }

  .carousel-card {
    position: absolute;
    cursor: pointer;
    border-radius: 8px;
    overflow: hidden;
    flex-shrink: 0;
    
    /* Smooth transitions for all properties */
    transition: 
      transform 0.5s cubic-bezier(0.4, 0, 0.2, 1),
      width 0.5s cubic-bezier(0.4, 0, 0.2, 1),
      height 0.5s cubic-bezier(0.4, 0, 0.2, 1),
      opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1),
      box-shadow 0.5s cubic-bezier(0.4, 0, 0.2, 1);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  /* Static mode (items < minItemsForCarousel) - all items same size */
  &:not(.has-multiple-items) {
    .carousel-track {
      height: auto;
      min-height: 280px;
    }

    .carousel-card {
      position: relative;
      width: 340px;
      height: 213px;
      opacity: 1;
      transform: none;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);

      &:hover {
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
      }
    }
  }

  /* Hidden items (only in carousel mode) */
  &.has-multiple-items .carousel-card.hidden,
  &.has-multiple-items .carousel-card.hidden-left,
  &.has-multiple-items .carousel-card.hidden-right {
    opacity: 0;
    pointer-events: none;
  }

  /* =============================================
     SCALE MODE - Center item larger, index[0] at center
     Desktop & Tablet: left/right 322x182, center 444x250
     Mobile: left/right 180x102, center 273x154
     ============================================= */
  &.has-multiple-items.scale-mode {
    .carousel-wrapper {
      height: 300px;
    }

    .carousel-card {
      /* Left item - Desktop/Tablet */
      &.left {
        width: 322px;
        height: 182px;
        opacity: 1;
        transform: translateX(-400px);
        z-index: 1;
      }

      /* Right item - Desktop/Tablet */
      &.right {
        width: 322px;
        height: 182px;
        opacity: 1;
        transform: translateX(400px);
        z-index: 1;
      }

      /* Center item - Desktop/Tablet */
      &.center {
        width: 444px;
        height: 250px;
        transform: translateX(0);
        z-index: 2;
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
        opacity: 1;
      }

      /* Hidden items */
      &.hidden,
      &.hidden-left,
      &.hidden-right {
        opacity: 0;
        pointer-events: none;
        transform: translateX(0) scale(0.5);
      }
    }
  }

  /* =============================================
     SLIDE MODE - Infinite scroll, index[0] at left
     ============================================= */
  &.slide-mode.has-multiple-items {
    .carousel-wrapper {
      height: 300px;
    }

    .carousel-track-container {
      width: 100%;
      overflow: hidden;
      position: relative;
      height: 100%;
      padding-left: 60px;
    }

    .carousel-track-slide {
      position: absolute;
      top: 50%;
      left: 0;
      display: flex;
      align-items: center;
      gap: 40px;
      will-change: transform;
      padding-left: 60px;
      
      &.transitioning {
        transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
      }
    }

    .carousel-card-slide {
      flex-shrink: 0;
      width: 400px;
      height: 250px;
      border-radius: 8px;
      overflow: hidden;
      cursor: pointer;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
      transition: box-shadow 0.3s ease;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      &:hover {
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
      }

      &.first-visible {
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
      }
    }
  }

  /* Dot indicators */
  .dot-indicators {
    display: flex;
    justify-content: center;
    gap: 8px;
  }

  .dot {
    width: 40px;
    height: 8px;
    border-radius: 2px;
    background: ${theme.palette.grey[300]};
    cursor: pointer;
    transition: background 0.3s ease, transform 0.2s ease;

    &.active {
      background: ${theme.palette.primaryV2.main};
    }

    &:hover {
      background: ${theme.palette.grey[400]};
      transform: scaleY(1.5);
    }

    &.active:hover {
      background: ${theme.palette.primaryV2.main};
    }
  }

  /* =============================================
     RESPONSIVE STYLES
     ============================================= */

  /* Tablet - same as desktop for scale mode */
  ${theme.breakpoints.down('lg')} {
    .carousel-wrapper {
      padding: 48px 0px;
      height: 300px;
    }

    &:not(.has-multiple-items) {
      .carousel-track {
        height: auto;
        min-height: 220px;
        gap: 16px;
      }

      .carousel-card {
        width: 280px;
        height: 175px;
      }
    }

    /* Tablet keeps same sizes as desktop: left/right 322x182, center 444x250 */
    &.has-multiple-items.scale-mode {
      .carousel-card {
        &.left {
          width: 322px;
          height: 182px;
          transform: translateX(-380px);
        }

        &.right {
          width: 322px;
          height: 182px;
          transform: translateX(380px);
        }

        &.center {
          width: 444px;
          height: 250px;
        }
      }
    }

    &.slide-mode.has-multiple-items {
      .carousel-wrapper {
        height: 250px;
      }

      .carousel-track-container {
        padding-left: 50px;
      }

      .carousel-track-slide {
        gap: 30px;
        padding-left: 50px;
      }

      .carousel-card-slide {
        width: 320px;
        height: 200px;
      }
    }
  }

  /* Mobile - left/right 180x102, center 273x154 */
  ${theme.breakpoints.down('md')} {
    .carousel-wrapper {
      padding: 24px 50px;
      height: 200px;
    }

    &:not(.has-multiple-items) {
      .carousel-track {
        height: auto;
        min-height: 180px;
        gap: 12px;
        flex-wrap: wrap;
      }

      .carousel-card {
        width: 240px;
        height: 150px;
      }
    }

    &.has-multiple-items.scale-mode {
      .carousel-card {
        &.left {
          width: 180px;
          height: 102px;
          transform: translateX(-230px);
        }

        &.right {
          width: 180px;
          height: 102px;
          transform: translateX(230px);
        }

        &.center {
          width: 273px;
          height: 154px;
        }
      }
    }

    &.slide-mode.has-multiple-items {
      .carousel-wrapper {
        height: 200px;
      }

      .carousel-track-container {
        padding-left: 40px;
      }

      .carousel-track-slide {
        gap: 20px;
        padding-left: 40px;
      }

      .carousel-card-slide {
        width: 260px;
        height: 163px;
      }
    }
  }

  /* Small mobile - same as mobile: left/right 180x102, center 273x154 */
  ${theme.breakpoints.down('sm')} {
    .carousel-wrapper {
      padding: 48px 0px;
      height: 200px;
    }

    &:not(.has-multiple-items) {
      .carousel-track {
        height: auto;
        min-height: 140px;
        gap: 8px;
        flex-wrap: wrap;
      }

      .carousel-card {
        width: 160px;
        height: 100px;
      }
    }

    &.has-multiple-items.scale-mode {
      .carousel-card {
        &.left {
          width: 180px;
          height: 102px;
          transform: translateX(-220px);
        }

        &.right {
          width: 180px;
          height: 102px;
          transform: translateX(220px);
        }

        &.center {
          width: 273px;
          height: 154px;
        }
      }
    }

    &.slide-mode.has-multiple-items {
      .carousel-wrapper {
        height: 150px;
      }

      .carousel-track-container {
        padding-left: 20px;
      }

      .carousel-track-slide {
        gap: 12px;
        padding-left: 20px;
      }

      .carousel-card-slide {
        width: 200px;
        height: 125px;
      }
    }
  }

  /* Large desktop - same as desktop: left/right 322x182, center 444x250 */
  ${theme.breakpoints.up('xl')} {
    .carousel-wrapper {
      max-width: 1600px;
      height: 300px;
    }

    &:not(.has-multiple-items) {
      .carousel-track {
        height: auto;
        min-height: 320px;
        gap: 32px;
      }

      .carousel-card {
        width: 400px;
        height: 250px;
      }
    }

    &.has-multiple-items.scale-mode {
      .carousel-card {
        &.left {
          width: 322px;
          height: 182px;
          transform: translateX(-420px);
        }

        &.right {
          width: 322px;
          height: 182px;
          transform: translateX(420px);
        }

        &.center {
          width: 444px;
          height: 250px;
        }
      }
    }

    &.slide-mode.has-multiple-items {
      .carousel-wrapper {
        height: 350px;
      }

      .carousel-track-container {
        padding-left: 80px;
      }

      .carousel-track-slide {
        gap: 50px;
        padding-left: 80px;
      }

      .carousel-card-slide {
        width: 480px;
        height: 300px;
      }
    }
  }
`,
);

export default CarouselStyle;
